import CheckoutSessionResultAlert from "components/Core/AccountInfo/Billing/CheckoutSessionResultAlert";
import useProcessStripeCheckoutSuccess from "components/Core/AccountInfo/Billing/useProcessStripeCheckoutSuccess";
import PageLoadingSpinner from "components/Shared/PageLoadingSpinner";
import { assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import SignupCheckoutSuccessContinueButton from "./SignupCheckoutSuccessContinueButton";
import SignupPlanSelection from "./SignupPlanSelection";
import useCompleteSignupPlanSelection from "./useCompleteSignupPlanSelection";

type Props = {
  onContinue: () => Promise<void>;
  selectedPlanSlug?: string;
  sessionId?: string;
};

export default function SignupPlanStep({
  onContinue,
  selectedPlanSlug,
  sessionId,
}: Props) {
  const brandId = useCurrentBrandId();

  const {
    completeSignupPlanSelection,
    isLoading: isCompleteSignupPlanSelectionLoading,
  } = useCompleteSignupPlanSelection();
  const checkout = useProcessStripeCheckoutSuccess({
    brandId,
    sessionId,
  });

  if (checkout.isProcessing || isCompleteSignupPlanSelectionLoading) {
    return <PageLoadingSpinner minHeight="12rem" />;
  }

  return (
    <>
      {checkout.hasResult && (
        <CheckoutSessionResultAlert
          alertProps={{ mb: 6 }}
          error={checkout.error}
          isLoading={checkout.loading}
          paymentStatus={checkout.paymentStatus}
        />
      )}

      {checkout.isSucceeded && (
        <SignupCheckoutSuccessContinueButton
          onContinue={async () => {
            const accessPlanId =
              checkout.data?.processStripeCheckoutSuccess.brand.accessPlanId;
            assertPresence(
              accessPlanId,
              "Expected checkout to sync an access plan.",
            );
            assertPresence(
              brandId,
              "Expected a current brand ID to complete signup plan selection.",
            );
            await completeSignupPlanSelection({
              accessPlanId,
              brandId,
            });
            await onContinue();
          }}
        />
      )}

      {!checkout.isSucceeded && (
        <SignupPlanSelection
          onContinue={onContinue}
          selectedPlanSlug={selectedPlanSlug}
        />
      )}
    </>
  );
}
